import React, { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Dropdown, Typography } from '../../../../../components';
import { useAddRemoveIgnoredOptimizationsMutation } from '../../../../../api/discovery/inventory';
import { useNotificationContext } from '../../../../../providers/NotificationProvider';
import { BaseDiscoveryTopBar } from '../../../shared';
import { WarningModal } from '../../../../../components';
import { hasValidIP } from '../../../helpers';
import { SEND_TO_MIGRATION_WARNING, MARK_READY_WARNING } from '../../../../../constants/discovery';
import {
    ellipsisDropdownLabels,
    selectableColumns,
    discoveryInventoryDropdownFilters,
    ignoreAllOptimizationsConfig,
} from './constants';
import { DISCOVERY_ROUTE_CONFIG } from '../../../../../constants/routes';
import { UploadOutlined, DownOutlined, MoreOutlined, TagsOutlined, DeleteOutlined } from '@ant-design/icons';

import './InventoryTopBar.scss';

const { Text } = Typography;

/**
 * InventoryTopBar - Extends BaseDiscoveryTopBar with inventory-specific actions
 * Adds: Import dropdown, Send to Migration, Ignore Optimizations, Tags, Delete
 */
export const InventoryTopBar = ({
    selectedColumns,
    handleColumnSelection,
    setSearchValue,
    searchValue,
    handleFilterDiscoveryInventoryData,
    resetFilters,
    refetchInventoryData,
    selectedRowKeys = [],
    selectedRows,
    selectedFilters,
    setSelectedFilters,
    handleChangeStatus,
    moveGroupsAndApps = false,
    onOpenManageModal,
    setShowSendToMigrationModal,
}) => {
    const navigate = useNavigate();
    const orgId = useMemo(() => sessionStorage.getItem('userOrg'), []);
    const { notifySuccess, notifyError } = useNotificationContext();
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [warningModalBody, setWarningModalBody] = useState('');

    const [addRemoveIgnoredOptimizations] = useAddRemoveIgnoredOptimizationsMutation();

    const handleChangeReadyStatus = useCallback(
        (vmIds, status) => {
            if (status === 'ready' && selectedRows.some((row) => !hasValidIP(row.ip_addresses))) {
                setWarningModalBody(MARK_READY_WARNING);
                setShowWarningModal(true);
                return;
            }
            handleChangeStatus(vmIds, status);
        },
        [handleChangeStatus, selectedRows],
    );

    const handleSendToMigration = useCallback(() => {
        if (selectedRows.some((row) => row.migration_ready !== 'ready')) {
            setWarningModalBody(SEND_TO_MIGRATION_WARNING);
            setShowWarningModal(true);
            return;
        }
        setShowSendToMigrationModal(true);
    }, [setShowSendToMigrationModal, selectedRows]);

    const handleBulkIgnoreOptimizations = useCallback(async () => {
        try {
            await addRemoveIgnoredOptimizations({
                orgId,
                vmIds: selectedRowKeys,
                action: 'add',
                optimizations: ignoreAllOptimizationsConfig,
            }).unwrap();
            notifySuccess({ message: 'Optimizations ignored successfully.' });
            refetchInventoryData();
        } catch (error) {
            notifyError({ message: 'Failed to ignore optimizations.' });
            console.error('Failed to ignore optimizations:', error);
        }
    }, [addRemoveIgnoredOptimizations, selectedRowKeys, refetchInventoryData, orgId, notifySuccess, notifyError]);

    const ellipsisHandlers = useMemo(
        () => ({
            'Ignore Optimizations': () => handleBulkIgnoreOptimizations(),
            'Download CSV': () => console.log('Download CSV handler'),
            Edit: () => console.log('Edit handler'),
        }),
        [handleBulkIgnoreOptimizations],
    );

    const importButtonDropdown = useMemo(
        () => [
            { key: '1', label: 'RVTools', onClick: () => navigate(DISCOVERY_ROUTE_CONFIG.rvTools.path) },
            { key: '2', label: 'AWS Transform', onClick: () => navigate(DISCOVERY_ROUTE_CONFIG.awsTransform.path) },
        ],
        [navigate],
    );

    const statusButtonDropdown = useMemo(
        () => [
            { key: '1', label: 'Mark Ready', onClick: () => handleChangeReadyStatus(selectedRowKeys, 'ready') },
            { key: '2', label: 'Mark Not Ready', onClick: () => handleChangeReadyStatus(selectedRowKeys, 'not_ready') },
        ],
        [handleChangeReadyStatus, selectedRowKeys],
    );

    const ellipsisDropdown = useMemo(
        () =>
            ellipsisDropdownLabels.map((item) => ({
                ...item,
                label: <Text>{item.label}</Text>,
                disabled: item.label === 'Ignore Optimizations' && selectedRowKeys.length === 0,
                onClick: ellipsisHandlers[item.label],
            })),
        [selectedRowKeys.length, ellipsisHandlers],
    );

    // Render import section
    const renderImportSection = useCallback(() => {
        if (moveGroupsAndApps) return null;

        return (
            <Dropdown menu={{ items: importButtonDropdown }} placement="bottomRight" arrow>
                <Button shape="round" type="primary" icon={<UploadOutlined />}>
                    Import <DownOutlined />
                </Button>
            </Dropdown>
        );
    }, [moveGroupsAndApps, importButtonDropdown]);

    // Render bulk actions
    const renderBulkActions = useCallback(
        ({ hasSelectedRows }) => (
            <>
                <Dropdown menu={{ items: statusButtonDropdown }} placement="bottomRight" arrow>
                    <Button shape="round" type="primary">
                        Change Status <DownOutlined />
                    </Button>
                </Dropdown>
                {moveGroupsAndApps && (
                    <Button shape="round" type="primary" onClick={onOpenManageModal}>
                        Assign
                    </Button>
                )}
                {!moveGroupsAndApps && (
                    <>
                        <Button shape="round" type="primary" onClick={() => handleSendToMigration()}>
                            Send to Migration
                        </Button>
                        <Button shape="circle" icon={<TagsOutlined />} />
                        {/* <Button shape="circle" icon={<DeleteOutlined />} /> */}
                    </>
                )}
            </>
        ),
        [statusButtonDropdown, moveGroupsAndApps, onOpenManageModal, handleSendToMigration],
    );

    // Render extra actions (ellipsis menu)
    const renderExtraActions = useCallback(() => {
        if (moveGroupsAndApps) return null;

        return (
            <Dropdown trigger={['click']} placement="right" menu={{ items: ellipsisDropdown }}>
                <Button type="text" icon={<MoreOutlined className="table-row-actions" />} />
            </Dropdown>
        );
    }, [moveGroupsAndApps, ellipsisDropdown]);

    return (
        <>
            <BaseDiscoveryTopBar
                selectedColumns={selectedColumns}
                handleColumnSelection={handleColumnSelection}
                selectableColumns={selectableColumns}
                setSearchValue={setSearchValue}
                searchValue={searchValue}
                searchPlaceholder="Search by IP, VM Name, or Hostname"
                searchTooltip="Search by IP, VM Name, or Hostname"
                handleFilterData={handleFilterDiscoveryInventoryData}
                resetFilters={resetFilters}
                selectedFilters={selectedFilters}
                setSelectedFilters={setSelectedFilters}
                filterOptions={discoveryInventoryDropdownFilters}
                refetchData={refetchInventoryData}
                selectedRowKeys={selectedRowKeys}
                renderImportSection={renderImportSection}
                renderBulkActions={renderBulkActions}
                renderExtraActions={renderExtraActions}
                className="inventory-top-bar"
            />
            <WarningModal open={showWarningModal} onOk={() => setShowWarningModal(false)}>
                {warningModalBody}
            </WarningModal>
        </>
    );
};
